import React from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ExternalLink, Heart, MessageCircle, Play } from 'lucide-react';
import { instagramPosts } from './mock';

const InstagramSection = () => {
  const handlePostClick = (post) => {
    window.open(post.url, '_blank');
  };

  const handleInstagramClick = () => {
    window.open('https://instagram.com/tecnote_store', '_blank');
  };

  return (
    <section id="projects" className="py-20 bg-gray-900 relative overflow-hidden">
      {/* Background video overlay */}
      <div className="absolute inset-0 bg-gray-900/90" />
      
      <div className="container mx-auto px-4 relative">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Quer conhecer a
            <span className="block text-orange-500">Tecnote?</span>
          </h2>
          <p className="text-xl text-gray-300">
            Acompanhe nossos projetos, dicas e bastidores das montagens. 
            Cada PC é único e tem sua própria história.
          </p>
        </div>

        {/* Instagram Posts Grid - Masonry Style similar to reference */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-12">
          {instagramPosts.map((post, index) => (
            <div 
              key={post.id} 
              className={`group cursor-pointer ${
                index === 0 || index === 4 ? 'row-span-2' : ''
              }`}
              onClick={() => handlePostClick(post)}
            >
              <div className="relative overflow-hidden rounded-lg bg-gray-800 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 h-full">
                {/* Image */}
                <div className="relative aspect-square overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="flex items-center justify-between text-white">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-1">
                            <Heart className="w-4 h-4" />
                            <span className="text-sm">{post.likes}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <MessageCircle className="w-4 h-4" />
                            <span className="text-sm">{post.comments}</span>
                          </div>
                        </div>
                        {post.type === 'reel' && (
                          <div className="flex items-center space-x-1">
                            <Play className="w-4 h-4" />
                            <span className="text-sm">Reel</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Post Type Icon */}
                  <div className="absolute top-4 right-4">
                    {post.type === 'reel' ? (
                      <div className="w-8 h-8 bg-black/60 rounded-full flex items-center justify-center">
                        <Play className="w-4 h-4 text-white" />
                      </div>
                    ) : (
                      <div className="w-8 h-8 bg-black/60 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Content - Hidden on smaller items */}
                <div className="p-4 block">
                  <h3 className="font-bold text-white mb-1 text-sm group-hover:text-orange-400 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-400 text-xs line-clamp-2">
                    {post.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button 
            size="lg" 
            onClick={handleInstagramClick}
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
            Confira todos os projetos no Instagram
          </Button>
          
          <p className="text-gray-400 mt-4">
            Siga @tecnote_store para acompanhar nossos trabalhos diários
          </p>
        </div>
      </div>
    </section>
  );
};

export default InstagramSection;